/* Aria Robotics — Neuigkeiten + Blog + Detailseiten.
 * News: statisch aus data.ts. Blog: aus PocketBase (published), mit Fallback
 * auf die statischen Daten, falls PB nicht erreichbar ist. */
import * as React from 'react';
import DOMPurify from 'dompurify';
import { Container, Section, Btn, Chip, Panel, Icon, ImageFrame, ImageSlot, fmtDate, go } from '@/ui';
import { AriaData as D } from '@/data';
import type { NewsItem, BlogItem } from '@/data';
import { loadPublishedPosts, loadPublishedPost, type PublicPost } from '@/lib/publicBlog';

// ---------- static article bodies (fallback rendering) ----------
interface Block { p?: string; h?: string; quote?: string; list?: string[]; }
const bodies: Record<string, Block[]> = {
  'auslieferung-welle-1': [
    { p: 'Nach sieben Jahren Entwicklung ist es soweit: Die ersten Nestor 01 ziehen in echte Haushalte ein. Wir beginnen in Leipzig, Berlin und München — bewusst in kleiner Zahl.' },
    { p: 'Wir hätten schneller ausliefern können. Aber ein Roboter, den man ins eigene Zuhause lässt, verdient mehr als eine Massenabfertigung. Jede Einrichtung wird von einem Aria-Techniker persönlich begleitet.' },
    { h: 'Warum wir langsam starten' },
    { p: 'Jede der ersten Familien hilft uns, Nestor besser zu machen. Wir hören zu, beobachten den Alltag und lernen — genau wie in unseren Pilothaushalten. Wachstum um jeden Preis war nie unser Ziel.' },
    { quote: 'Ein guter Gast drängt sich nicht auf. Er fragt, bevor er handelt.' },
    { p: 'Die nächste Welle folgt im Herbst 2026. Wer sich jetzt einen Platz sichert, rückt automatisch nach.' },
  ],
  'wie-viel-hausarbeit': [
    { p: 'Die ehrlichste Antwort vorweg: Nestor nimmt Ihnen nicht die ganze Hausarbeit ab. Aber er übernimmt genug, dass Sie es deutlich merken — im Schnitt rund zwölf Stunden pro Woche.' },
    { h: 'Was Nestor zuverlässig übernimmt' },
    { p: 'Tisch decken und abräumen, den Geschirrspüler ein- und ausräumen, Wäsche sortieren und zusammenlegen, Flächen abwischen, aufräumen.' },
    { list: ['Küche: Tisch decken, Geschirrspüler, Flächen', 'Wäsche: sortieren, waschen, zusammenlegen', 'Ordnung: aufräumen nach Ihren Vorlieben'] },
    { h: 'Wo Nestor heute an Grenzen stößt' },
    { p: 'Treppen mit mehr als zwei Stufen steigt Nestor noch nicht sicher. Er kocht nicht mit offenem Feuer und ist kein Ersatz für Pflege oder die Aufsicht von Kindern.' },
    { quote: 'Wir versprechen lieber weniger und halten es.' },
    { h: 'Wie sich das verändert' },
    { p: 'Nestor lernt dazu — durch kostenfreie Software-Updates, die Sie freigeben, nie ungefragt.' },
  ],
};
function genericBody(item: NewsItem | BlogItem): Block[] {
  return [
    { p: item.teaser },
    { p: 'Diesen Beitrag füllen wir in Kürze mit dem vollständigen Text. Die Struktur steht, die Inhalte folgen redaktionell.' },
  ];
}
function ArticleBody({ blocks }: { blocks: Block[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {blocks.map((b, i) => {
        if (b.h) return <h2 key={i} style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-100)', margin: '10px 0 0' }}>{b.h}</h2>;
        if (b.quote) return <blockquote key={i} style={{ margin: 0, padding: '4px 0 4px 22px', borderLeft: '3px solid var(--accent-100)', fontSize: 21, fontWeight: 300, fontStyle: 'italic', color: 'var(--text-100)', lineHeight: 1.4 }}>{b.quote}</blockquote>;
        if (b.list) return (
          <ul key={i} style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {b.list.map((li) => (
              <li key={li} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 17, color: 'var(--text-body)', lineHeight: 1.6 }}>
                <span style={{ color: 'var(--primary-200)', marginTop: 2, flex: 'none' }}><Icon name="check" size={20} /></span>{li}
              </li>
            ))}
          </ul>
        );
        return <p key={i} style={{ margin: 0, fontSize: 17.5, lineHeight: 1.7, color: 'var(--text-body)' }}>{b.p}</p>;
      })}
    </div>
  );
}
function AiNote() {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 40, padding: '14px 16px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <span style={{ color: 'var(--text-200)', flex: 'none', marginTop: 1 }}><Icon name="sparkles" size={18} /></span>
      <span style={{ fontSize: 13, color: 'var(--text-200)', lineHeight: 1.5 }}>Hinweis: Dieser Beitrag wurde mit Unterstützung von KI-Werkzeugen erstellt und redaktionell geprüft. Kennzeichnung gemäß EU-KI-Verordnung (EU AI Act).</span>
    </div>
  );
}

// ---------- News (static) ----------
export function News() {
  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(640px 380px at 80% 14%, rgba(74,144,226,0.22), transparent 60%)' }}>
        <Container>
          <div style={{ padding: '76px 0', maxWidth: 720 }}>
            <Chip tone="inverse" dot>Neuigkeiten</Chip>
            <h1 style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '18px 0 0', lineHeight: 1.04 }}>Was bei Aria gerade passiert</h1>
            <p style={{ fontSize: 19, fontWeight: 300, color: 'rgba(255,255,255,0.8)', marginTop: 14, maxWidth: '48ch' }}>Kurze Updates aus dem Unternehmen — zu Produkt, Fertigung und Sicherheit.</p>
          </div>
        </Container>
      </header>
      <Section tone="page">
        <Container narrow>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {D.news.map((n) => (
              <div key={n.slug} className="ar-row2" onClick={() => go('/neuigkeiten/' + n.slug)} style={{ cursor: 'pointer', display: 'grid', gridTemplateColumns: '150px 1fr', gap: 28, padding: '28px 0', borderTop: '1px solid var(--border-subtle)' }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-200)', letterSpacing: '0.04em' }}>{fmtDate(n.date)}</div>
                  <div style={{ marginTop: 10 }}><Chip tone="info">{n.tag}</Chip></div>
                </div>
                <div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-100)', margin: '0 0 8px' }}>{n.title}</h3>
                  <p style={{ fontSize: 16, color: 'var(--text-200)', lineHeight: 1.6, margin: '0 0 12px', maxWidth: '58ch' }}>{n.teaser}</p>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 600, color: 'var(--primary-200)' }}>Weiterlesen <Icon name="arrow-right" size={16} /></span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </div>
  );
}

// ---------- Blog (PocketBase, fallback static) ----------
interface DisplayPost { slug: string; title: string; summary: string; tag: string; meta: string; imageUrl?: string; imgSlot?: string; }

function fromPb(p: PublicPost): DisplayPost {
  return { slug: p.slug, title: p.title, summary: p.summary, tag: p.categoryName || 'Beitrag', meta: fmtDate(p.date), imageUrl: p.imageUrl };
}
function fromStatic(b: BlogItem): DisplayPost {
  return { slug: b.slug, title: b.title, summary: b.teaser, tag: b.tag, meta: b.read, imgSlot: b.img };
}

function PostImage({ item, id }: { item: DisplayPost; id: string }) {
  if (item.imageUrl) return <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />;
  return <ImageSlot id={id} shape="rect" src={item.imgSlot} placeholder="Titelbild" style={{ width: '100%', height: '100%' }} />;
}

export function Blog() {
  const [items, setItems] = React.useState<DisplayPost[] | null>(null);
  React.useEffect(() => {
    let alive = true;
    void loadPublishedPosts().then((pbPosts) => {
      if (!alive) return;
      if (pbPosts && pbPosts.length > 0) setItems(pbPosts.map(fromPb));
      else setItems(D.blog.map(fromStatic)); // fallback (PB empty or unreachable)
    });
    return () => { alive = false; };
  }, []);

  const feat = items?.[0];
  const rest = items ? items.slice(1) : [];

  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(640px 380px at 20% 14%, rgba(74,144,226,0.22), transparent 60%)' }}>
        <Container>
          <div style={{ padding: '76px 0', maxWidth: 720 }}>
            <Chip tone="inverse" dot>Blog</Chip>
            <h1 style={{ fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '18px 0 0', lineHeight: 1.04 }}>Gedanken, Ratgeber, Hintergründe</h1>
            <p style={{ fontSize: 19, fontWeight: 300, color: 'rgba(255,255,255,0.8)', marginTop: 14, maxWidth: '48ch' }}>Längere Beiträge darüber, was ein Roboter fürs Zuhause wirklich leisten sollte — und was Vertrauen bedeutet.</p>
          </div>
        </Container>
      </header>

      <Section tone="page">
        <Container>
          {!items ? (
            <div style={{ color: 'var(--text-200)', fontSize: 15 }}>Beiträge werden geladen…</div>
          ) : items.length === 0 ? (
            <div style={{ color: 'var(--text-200)', fontSize: 15 }}>Noch keine Beiträge veröffentlicht.</div>
          ) : (
            <>
              {feat && (
                <Panel variant="default" padding={0} hover onClick={() => go('/blog/' + feat.slug)} style={{ overflow: 'hidden', marginBottom: 32 }}>
                  <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr' }}>
                    <div style={{ position: 'relative', minHeight: 300 }}>
                      <PostImage item={feat} id="blog-feat" />
                    </div>
                    <div style={{ padding: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
                        <Chip tone="info">{feat.tag}</Chip>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-200)' }}>{feat.meta}</span>
                      </div>
                      <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-100)', margin: '0 0 10px', lineHeight: 1.15 }}>{feat.title}</h2>
                      <p style={{ fontSize: 16, color: 'var(--text-200)', lineHeight: 1.6, margin: '0 0 18px' }}>{feat.summary}</p>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600, color: 'var(--primary-200)' }}>Artikel lesen <Icon name="arrow-right" size={16} /></span>
                    </div>
                  </div>
                </Panel>
              )}
              {rest.length > 0 && (
                <div className="ar-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
                  {rest.map((b) => (
                    <Panel key={b.slug} variant="default" padding={0} hover onClick={() => go('/blog/' + b.slug)} style={{ overflow: 'hidden' }}>
                      <div style={{ aspectRatio: '16/9', position: 'relative' }}>
                        <PostImage item={b} id={`blog-${b.slug}`} />
                      </div>
                      <div style={{ padding: 26 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                          <Chip tone="info">{b.tag}</Chip>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-200)' }}>{b.meta}</span>
                        </div>
                        <h3 style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-100)', margin: '0 0 8px', lineHeight: 1.2 }}>{b.title}</h3>
                        <p style={{ fontSize: 15, color: 'var(--text-200)', lineHeight: 1.6, margin: 0 }}>{b.summary}</p>
                      </div>
                    </Panel>
                  ))}
                </div>
              )}
            </>
          )}
        </Container>
      </Section>
    </div>
  );
}

// ---------- Article ----------
export function Article({ kind, slug }: { kind: 'blog' | 'news'; slug: string }) {
  const backTo = kind === 'blog' ? '/blog' : '/neuigkeiten';
  const backLabel = kind === 'blog' ? 'Alle Blog-Beiträge' : 'Alle Neuigkeiten';

  const [pbPost, setPbPost] = React.useState<PublicPost | null | undefined>(kind === 'blog' ? undefined : null);
  React.useEffect(() => {
    if (kind !== 'blog') return;
    let alive = true;
    void loadPublishedPost(slug).then((p) => { if (alive) setPbPost(p); });
    return () => { alive = false; };
  }, [kind, slug]);

  if (kind === 'blog' && pbPost === undefined) {
    return <Section tone="page"><Container narrow><p style={{ color: 'var(--text-200)' }}>Beitrag wird geladen…</p></Container></Section>;
  }

  // PocketBase-backed blog article
  if (kind === 'blog' && pbPost) {
    const clean = DOMPurify.sanitize(pbPost.contentHtml);
    return (
      <div>
        <Section tone="page" style={{ paddingBottom: 40 }}>
          <Container narrow>
            <BackButton to={backTo} label={backLabel} />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
              {pbPost.categoryName && <Chip tone="info">{pbPost.categoryName}</Chip>}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-200)' }}>{fmtDate(pbPost.date)}</span>
            </div>
            <h1 style={{ fontSize: 'clamp(30px, 4.6vw, 46px)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-100)', margin: 0, lineHeight: 1.08, maxWidth: '20ch' }}>{pbPost.title}</h1>
          </Container>
        </Section>
        {pbPost.imageUrl && (
          <Container narrow style={{ marginBottom: 8 }}>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 24, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
              <img src={pbPost.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </Container>
        )}
        <Section tone="page" style={{ paddingTop: 48 }}>
          <Container narrow>
            {pbPost.summary && <p style={{ fontSize: 19, fontWeight: 300, lineHeight: 1.6, color: 'var(--text-200)', margin: '0 0 26px' }}>{pbPost.summary}</p>}
            <div className="ar-article-html" dangerouslySetInnerHTML={{ __html: clean }} />
            <AiNote />
            <div style={{ marginTop: 40, paddingTop: 28, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Btn variant="secondary" iconLeft="arrow-left" onClick={() => go(backTo)}>{backLabel}</Btn>
              <Btn variant="cta" iconRight="arrow-right" onClick={() => go('/kontakt')}>Nestor 01 vorbestellen</Btn>
            </div>
          </Container>
        </Section>
      </div>
    );
  }

  // Static fallback (news, or blog slug not in PB)
  const list = kind === 'blog' ? D.blog : D.news;
  const item = list.find((x) => x.slug === slug);
  if (!item) {
    return (
      <Section tone="page"><Container narrow>
        <p style={{ fontSize: 18, color: 'var(--text-200)' }}>Beitrag nicht gefunden.</p>
        <Btn variant="secondary" iconLeft="arrow-left" onClick={() => go(backTo)}>Zurück zur Übersicht</Btn>
      </Container></Section>
    );
  }
  const blocks = bodies[slug] ?? genericBody(item);
  const readSuffix = 'read' in item && item.read ? ' · ' + item.read : '';
  return (
    <div>
      <Section tone="page" style={{ paddingBottom: 40 }}>
        <Container narrow>
          <BackButton to={backTo} label={backLabel} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
            <Chip tone="info">{item.tag}</Chip>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-200)' }}>{fmtDate(item.date)}{readSuffix}</span>
          </div>
          <h1 style={{ fontSize: 'clamp(30px, 4.6vw, 46px)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-100)', margin: 0, lineHeight: 1.08, maxWidth: '20ch' }}>{item.title}</h1>
        </Container>
      </Section>
      {item.img && (
        <Container narrow style={{ marginBottom: 8 }}>
          <ImageFrame id={`art-${slug}`} ratio="16/9" src={item.img} placeholder="Beitragsbild" />
        </Container>
      )}
      <Section tone="page" style={{ paddingTop: 48 }}>
        <Container narrow>
          <ArticleBody blocks={blocks} />
          <AiNote />
          <div style={{ marginTop: 40, paddingTop: 28, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Btn variant="secondary" iconLeft="arrow-left" onClick={() => go(backTo)}>{backLabel}</Btn>
            <Btn variant="cta" iconRight="arrow-right" onClick={() => go('/kontakt')}>Nestor 01 vorbestellen</Btn>
          </div>
        </Container>
      </Section>
    </div>
  );
}

function BackButton({ to, label }: { to: string; label: string }) {
  return (
    <button onClick={() => go(to)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--primary-200)', padding: 0, marginBottom: 28 }}>
      <Icon name="arrow-left" size={16} /> {label}
    </button>
  );
}
